// Chakra imports
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Image,
  Input,
  Link,
  Spinner,
  Stack,
  Switch,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
// Assets
import NortewareLogo from "assets/img/norteware-logo.png";
import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import AuthCover from "layouts/AuthCover";
import { AuthContext } from "contexts/AuthContext";
import { KEYS } from "constants/keys";
import { ROUTES } from "constants/routes-path";
import { CUSTOMER_LOGIN_IMAGE, CUSTOMER_LOGO, CUSTOMER_DEFAULT_ROUTE, REACT_APP_COPYRIGHT, SHOW_SECONDARY_LOGO_IN_LOGIN } from "constants/config";
import { REACT_APP_GIT_BRANCH, REACT_APP_GIT_SHA, REACT_APP_GIT_TAG } from "constants/git-constants"

function SignInCover() {
  const { t } = useTranslation();
  const { login, showKeepConnected } = useContext(AuthContext);
  const { search } = useLocation();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errorMessage, setErrorMessage] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const textColor = useColorModeValue("gray.500", "white");
  const bgForm = useColorModeValue("white", "navy.800");
  const titleColor = useColorModeValue("gray.700", "blue.500");

  useEffect(() => {
    localStorage.removeItem(KEYS.token);
    localStorage.removeItem(KEYS.user);
    localStorage.removeItem(KEYS.keepConnected);
  }, []);

  const handleLogin = async () => {
    if (isLoading || !formData.email || !formData.password) return;
    setIsLoading(true);
    setErrorMessage(undefined);

    try {
      const data = await login(formData.email, formData.password);
      setIsLoading(false);

      if (!data) {
        setErrorMessage("system.messages.errors.unexpected");
      } else {
        const redirectTo = new URLSearchParams(search).get(KEYS.redirectTo);
        const user = JSON.parse(localStorage.getItem(KEYS.user) || "{}");
        const defaultRoute = getIndexFromPermission(user?.profilesPermissions);
        navigate(redirectTo && redirectTo !== ROUTES.login ? decodeURI(redirectTo) : defaultRoute ? defaultRoute : CUSTOMER_DEFAULT_ROUTE  || ROUTES.transportManagementPath);
      }
    } catch (error) {
      setIsLoading(false);
      setErrorMessage(error);
    }
  };

  const handleClear = () => {
    setFormData({ email: "", password: "" });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      return handleLogin();
    }

    if (e.key === "Escape") {
      e.preventDefault();
      return handleClear();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const getIndexFromPermission = (listaPermissoes) => {
    let perm = null;
    if (["Consulta de Infrações", "Consulta de Infrações para Agentes", "Consulta Geral de Infrações"].some(item => listaPermissoes.includes(item))) {
      perm = ROUTES.panel + ROUTES.transportManagement + ROUTES.trafficViolations;
    } else if (["Consulta de Veículos"].some(item => listaPermissoes.includes(item))) {
      perm = ROUTES.panel + ROUTES.transportManagement + ROUTES.searches + ROUTES.searchVehicle;
    } else if (["Pagamento de Infrações no Cartão"].some(item => listaPermissoes.includes(item))) {
      perm = ROUTES.panel + ROUTES.transportManagement + ROUTES.violationCardPayments;
    } else if (["Digitação de AITs", "Lavratura de AITs"].some(item => listaPermissoes.includes(item))) {
      perm = ROUTES.panel + ROUTES.transportManagement + ROUTES.addTrafficViolation;
    } else if (["Validação de Infrações", "Impressão de Lotes"].some(item => listaPermissoes.includes(item))) {
      perm = ROUTES.panel + ROUTES.transportManagement + ROUTES.violationAllotments;
    } else if (["Criação de Processos", "Atendimento de Processos", "Alocação de Processos", "Preencher Decisão Final de Processos"].some(item => listaPermissoes.includes(item))) {
      perm = ROUTES.panel + ROUTES.transportManagement + ROUTES.processes;
    } else if (["Criação de Permissionários"].some(item => listaPermissoes.includes(item))) {
      perm = ROUTES.panel + ROUTES.transportManagement + ROUTES.permittee;
    } else if (["Criação de Credenciais"].some(item => listaPermissoes.includes(item))) {
      perm = ROUTES.panel + ROUTES.transportManagement + ROUTES.parkingCredentials;
    } else if (["Relatórios Gerenciais", "Relatórios Financeiros", "Rastreamento de Agentes", "Relatórios de Permissionários", "Relatórios de Infrações"].some(item => listaPermissoes.includes(item))) {
      perm = ROUTES.panel + ROUTES.transportManagement + ROUTES.reports;
    }
    return perm;
  };

  return (
    <AuthCover image={CUSTOMER_LOGIN_IMAGE}>
      <Flex
        w="100%"
        alignItems="center"
        justifyContent="center"
        mb="60px"
        mt={{ base: "60px", md: "160px" }}
      >
        <Flex
          zIndex="2"
          direction="column"
          w="445px"
          borderRadius="15px"
          p={{ base: "40px", lg: "40px", sm: "30px" }}
          mx={{ base: "100px", sm: "25px" }}
          mb={{ base: "20px", md: "auto" }}
          bg={bgForm}
          boxShadow={useColorModeValue("0px 5px 14px rgba(0, 0, 0, 0.05)", "unset")}
        >
          <Stack
              direction={{ base: "column", md: "row" }}
              spacing="12px"
              align="center"
              justify="center">
            <Image
              src={CUSTOMER_LOGO}
              alt="Logo 2"
              w="200px"
              h="200px"
              maxH="100px"
              objectFit="contain"
            />
            {SHOW_SECONDARY_LOGO_IN_LOGIN && <Box display={{ base: "block", md: "block", sm: "none" }} w="1px" h="20px" bg={"white"} />}
            {SHOW_SECONDARY_LOGO_IN_LOGIN && <Image
              src={NortewareLogo}
              alt="Logo 1"
              w="100px"
              h="100px"
              maxH="100px"
              objectFit="contain"
            />}
          </Stack>
          <Text
            fontSize="xl"
            color={textColor}
            fontWeight="bold"
            textAlign="center"
            mb="22px"
            mt="22px"
          >
            {t("login.labels.description")}
          </Text>

          <FormControl>
            <FormLabel ms="4px" fontSize="sm" fontWeight="normal" color={textColor}>
              {t("login.form.fields.registration.label")}
            </FormLabel>
            <Input
              variant="auth"
              id="email"
              name="email"
              value={formData.email}
              fontSize="sm"
              type="text"
              placeholder={t("login.form.fields.registration.placeholder")}
              mb="24px"
              size="lg"
              onKeyDown={handleKeyDown}
              onChange={handleChange}
              color={textColor}
            />

            <FormLabel ms="4px" fontSize="sm" fontWeight="normal" color={textColor}>
              {t("login.form.fields.password.label")}
            </FormLabel>
            <Input
              variant="auth"
              id="password"
              name="password"
              value={formData.password}
              fontSize="sm"
              type="password"
              placeholder={t("login.form.fields.password.placeholder")}
              mb="8px"
              size="lg"
              onKeyDown={handleKeyDown}
              onChange={handleChange}
              color={textColor}
            />

            {errorMessage && (
              <Text color="red.500" fontSize="sm" mb="24px">
                {t(errorMessage)}
              </Text>
            )}

            {showKeepConnected && (
              <FormControl display="flex" alignItems="center" mb="24px">
                <Switch id="remember-login" colorScheme="blue" me="10px" />
                <FormLabel htmlFor="remember-login" mb="0" fontWeight="normal" color={textColor}>
                  {t("login.form.fields.keep-signed-in.label")}
                </FormLabel>
              </FormControl>
            )}

            <Button
              w="100%"
              h="45"
              mb="24px"
              id="login-button"
              fontSize="sm"
              variant="dark"
              cursor={isLoading ? "default" : "pointer"}
              onClick={handleLogin}
            >
              {isLoading ? (
                <Spinner />
              ) : (
                <Text>
                  {t("login.actions.sign-in")}
                </Text>
              )}
            </Button>
          </FormControl>
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            fontSize={"12px"}
            maxW="100%"
            mt="0px"
          >
            <Text color={textColor} fontWeight="medium">
              {REACT_APP_COPYRIGHT}{" "}
              <Link
                color={titleColor}
                ms="5px"
                fontSize={"12px"}
                href="http://www.norteware.com.br/"
                fontWeight="bold"
                target="_blank"
                rel="noopener noreferrer"
                isExternal
              >
                Norteware Serviços de Software LTDA
              </Link>
            </Text>
          </Flex>
          {/* <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            maxW="100%"
          >
            <Text color={textColor} fontWeight="medium">
              {[REACT_APP_GIT_BRANCH, REACT_APP_GIT_TAG ? `v${REACT_APP_GIT_TAG}` : null, REACT_APP_GIT_SHA ? REACT_APP_GIT_SHA.slice(0, 7) : null]
                .filter(Boolean)
                .join(" | ") || "—"}
            </Text>
          </Flex> */}
        </Flex>
      </Flex>
    </AuthCover>
  );
}

export default SignInCover;
